/**
 * CapacitySlider: the centrepiece of the gateway.
 *
 * The whole purchase decision runs through this one control, so it carries
 * more craft than anything else in the repo.
 *
 * ── THE LADDER, WHICH IS THE WHOLE IDEA ───────────────────────────────────
 * The control is not a rail with a fill on it. It is FIVE CAPSULES, one per
 * band, on a five column grid, with a gap at every tranche boundary. The
 * previous build ran a fill from the left edge to the thumb centre, and at
 * the 150 student minimum that fill was 14px wide and entirely hidden
 * underneath the thumb: the control read as switched off at exactly the
 * position it ships in. A capsule is filled or it is not, so band one is a
 * filled segment a fifth of the track wide and the control is visibly ON at
 * every stop.
 *
 * The same change makes the structure legible without a word of copy. The
 * four boundaries used to be 2px hairlines drawn on the bar; they are now the
 * gaps between capsules. The band ahead of the buyer is tinted rather than
 * dead, which is the page's own argument (the per student rate falls at every
 * step) stated as geometry. And a capsule has its own box, so the one being
 * entered can SWELL, which is where the boundary moment lives.
 *
 * ── THE FIVE PARTS ────────────────────────────────────────────────────────
 *   1. THE FIGURE       $400 monthly, huge. The per student equivalent under
 *                       it at body size, IN THE CADENCE ON SCREEN: $2.67 per
 *                       student, per month on Monthly, $27 per year on Annual
 *                       (Thomas, Sep 14 2026). "Billed monthly" at meta size:
 *                       the Monthly view no longer prints "$4,000 billed
 *                       annually", which read as how a monthly plan is billed.
 *   2. THE HEAD         one line, not a form. The question on the left, the
 *                       typed cohort on the right as a numeral on a hairline
 *                       with its unit beside it. The old head was a label, a
 *                       boxed input and the sentence "Optional. Or move the
 *                       slider.", which is three pieces of chrome saying one
 *                       thing and reads as a settings screen.
 *   3. THE LADDER       five capsules and, on the SAME grid, a scale carrying
 *                       each band's ceiling and its per student rate. The
 *                       descent 27, 23, 20, 18 is therefore readable off the
 *                       control with no prose beside it, and every figure
 *                       sits under its own band by construction rather than
 *                       by arithmetic that can drift.
 *   4. THE THUMB        34px on a 46px hit area, four distinct states, and a
 *                       badge above it carrying the band CEILING. It is the
 *                       heaviest object on the page because it is the one the
 *                       buyer is meant to reach for.
 *   5. THE BAND CARDS   five cards under the same hairline, inside the same
 *                       card, wired as a radiogroup. Picking one moves the
 *                       slider; moving the slider checks one. One control.
 *
 * ── WHAT IT IS BUILT OUT OF ───────────────────────────────────────────────
 * Sixteen live pricing pages were probed for a real quantity slider and
 * exactly one still ships one, so the references had to come from the one
 * product plus the component libraries that set the craft standard.
 *
 *   Sentry   the value is an INDEX into the band array, never a student count
 *            and never a price. `min=0 max=BANDS.length-1 step=1` means the
 *            browser's own snapping IS the tranche snapping, so there is no
 *            custom snap logic and an illegal price is unreachable by
 *            construction rather than rejected after the fact. Their
 *            aria-valuetext carries the whole offer, not "3 of 5".
 *            Their 6px-tall input is the one thing NOT copied: the track is
 *            the entire hit area there, which fails WCAG 2.2 SC 2.5.8.
 *   Radix    the principle that everything which must stay in register with
 *            the thumb reads ONE expression. Here that is --eg-x, the centre
 *            of the active capsule, which the thumb, the badge caret and the
 *            boundary ping all read, so they cannot separate by a pixel.
 *            Their thumb-inset correction is no longer needed: a capsule
 *            centre is already inset by half a capsule, so the overhang the
 *            correction existed to fix cannot happen. Measured at all five
 *            stops, thumb centre against capsule centre: 0.01px.
 *   MUI      padding on the rail, giving a 46px touch target from a 26px
 *            visual, and `touch-action: none` so a finger drag does not
 *            scroll the page.
 *   shadcn   a white thumb with an accent border rather than a filled disc,
 *            and the same ring on hover and on focus-visible.
 *
 * ── THE DIGITS SNAP, EVERYTHING ELSE MOVES ────────────────────────────────
 * base.css states the governing rule in capitals: NUMBERS DO NOT ANIMATE. A
 * counting figure reads as unstable, and in an analytics product that is the
 * one impression this surface cannot afford. It also makes the reader wait to
 * learn the thing they just asked for.
 *
 * So the resolution is a split, and it is the whole motion design:
 *
 *   SNAP, in one frame     the monthly figure, the per-student figure, the
 *                          annual total, the scale emphasis, the checked band
 *                          card.
 *   MOVE, over 180ms       the capsules' colour and height, the thumb, the
 *                          badge, the badge caret, the rule under the price.
 *                          Everything positional reads `--eg-x`, so it all
 *                          arrives together.
 *   FIRE ONCE, on a cross  a crimson ring pinging out from the thumb, a
 *                          sheen sweeping ACROSS the capsule just entered in
 *                          the direction of travel, and the same sheen on the
 *                          rule under the price. All three are keyed on a
 *                          counter that only increments when the band
 *                          actually changes, so React remounts them and the
 *                          animation restarts with no timers, no state
 *                          machine and no exit animation to coordinate.
 *
 * The boundary moment is therefore FELT (a pulse and a surge) without any
 * number moving. That is requirement 4 discharged in both directions.
 *
 * Transitions are NOT killed while dragging, which reverses the previous
 * build. With `step=1` over five stops the thumb can only ever occupy five
 * positions, so "1:1 with the finger" is not achievable and switching the
 * transition off only makes the thumb teleport between stops. A 180ms glide
 * between stops is what makes a five-stop control feel like a slider rather
 * than a segmented button. `is-drag` still exists; it drives the enlarged
 * thumb and the wider halo, not the timing.
 *
 * `prefers-reduced-motion` is handled globally in base.css, which forces every
 * animation and transition inside [data-surface="enterprise"] to 0.01ms. The
 * pulse and the surge both start at `opacity: 0` and are driven TO their
 * visible state and back by the keyframes with `forwards`, so at 0.01ms they
 * land invisible rather than freezing on screen. Nothing here needs a second
 * media query and the function is never removed.
 *
 * ── TABULAR NUMERALS ARE NOT A DETAIL ─────────────────────────────────────
 * Without `font-variant-numeric: tabular-nums` and a min-width on the numeral,
 * "$400" and "$1,800" have different glyph widths and the whole panel twitches
 * on every step. That reads as instability far more than an instant swap does.
 *
 * ── THE TYPED FIELD SURVIVES FROM THE PREVIOUS BUILD ──────────────────────
 * A buyer who already knows their cohort size should not have to drag to it.
 * It is a text input with inputMode numeric, never type=number: native
 * spinners cannot be styled consistently and silently accept "e" and "-".
 * Typing a number moves the slider. Dragging the slider does not erase what
 * was typed, it only re-selects the band, and the headroom line then says
 * plainly that the chosen band is above or below the typed cohort.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import { cn } from "~/app/lib/cn";
import { formatCount, formatMoney } from "~/app/lib/format";
import {
  BANDS,
  bandById,
  bandIndexOf,
  capacityFromSlider,
  capacityFromTyped,
  cohortFieldPlaceholder,
  cohortFieldValue,
  describeBandForAria,
  annualSavingPercent,
  describeBandForPrint,
  describeCapacityNote,
  describeEquivalence,
  formatPerStudent,
  parseStudentCount,
  type BandId,
  type CapacityBand,
  type CapacityChoice,
} from "~/app/lib/pricing";

export type Billing = "monthly" | "annual";

export interface CapacitySliderProps {
  /**
   * THE VALUE, singular. The band and the cohort are one object because they
   * are one decision: see the "ONE VALUE, TWO CONTROLS" block in pricing.ts
   * for the bug that two separate pieces of state produced. The page owns it
   * so the checkout link reads the same state the card shows.
   */
  choice: CapacityChoice;
  onChoiceChange: (next: CapacityChoice) => void;
  billing: Billing;
  onBillingChange: (billing: Billing) => void;
  /** The primary action and its one text alternative. Owned by the page. */
  actions?: ReactNode;
  className?: string;
}

const LAST_INDEX = BANDS.length - 1;

/** How many bands PageUp and PageDown move. Two, so the key is worth pressing
 *  on a five-stop ladder; one would duplicate the arrow keys. */
const PAGE_STEP = 2;

export function CapacitySlider({
  choice,
  onChoiceChange,
  billing,
  onBillingChange,
  actions,
  className,
}: CapacitySliderProps) {
  const { bandId, students } = choice;
  const base = useId();
  const sliderId = `${base}-slider`;
  const typedId = `${base}-typed`;
  const liveId = `${base}-live`;
  const noteId = `${base}-note`;
  const billingLabelId = `${base}-billing`;
  const bandsLabelId = `${base}-bands`;

  const band = bandById(bandId);
  const index = bandIndexOf(bandId);
  const pct = LAST_INDEX === 0 ? 0 : (index / LAST_INDEX) * 100;

  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  /* The field's own text. It is a DRAFT rather than the value itself because
     a half-typed "1," must survive the keystroke that produced it, and
     formatting the value on every render would fight the caret. It is
     reconciled with the value below: anything the slider does replaces it. */
  const [draft, setDraft] = useState<string>(() => cohortFieldValue(choice));
  const [live, setLive] = useState("");

  /* The boundary moment. `pulse` only increments when the band actually
     changed, so the ring and the sheen never fire on first paint and never
     fire twice for one crossing. `direction` is carried because a step DOWN
     the ladder should not read as a celebration: the sheen runs the other
     way. */
  const previousBand = useRef<BandId>(bandId);
  const [pulse, setPulse] = useState(0);
  const [pulseDown, setPulseDown] = useState(false);

  useEffect(() => {
    if (previousBand.current === bandId) return;
    setPulseDown(bandIndexOf(bandId) < bandIndexOf(previousBand.current));
    previousBand.current = bandId;
    setPulse((n) => n + 1);
  }, [bandId]);

  /* THE FIELD FOLLOWS THE VALUE. This is the half of item 9 that closes the
     loop: a slider move writes a new cohort and the text in the box is
     rewritten to it.

     IT IS DONE DURING RENDER, NOT IN AN EFFECT, and that is not a style
     preference. An effect is passive: it runs after the browser has painted,
     so the field would show the OLD number for one frame every time the
     slider moved. Measured before this was changed, driving the range input
     and reading the field in the same task returned the previous stop's
     number at every stop: exactly the disagreement item 9 exists to remove,
     one frame wide. React's documented "adjusting state when a prop changes"
     pattern re-renders immediately, before anything is painted, so there is
     no frame in which the two controls disagree.

     The guard is what keeps typing usable: if the draft already parses to the
     value, it is left exactly as typed, so a half-finished "1," survives and
     the caret never moves. */
  const [syncedTo, setSyncedTo] = useState<number | null>(students);
  if (syncedTo !== students) {
    setSyncedTo(students);
    if (parseStudentCount(draft) !== students) setDraft(cohortFieldValue(choice));
  }

  /* A pointer released outside the input still ends the drag. Without this the
     control keeps its enlarged thumb forever after one drag that ended off
     the track. */
  useEffect(() => {
    if (!dragging) return;
    const end = (): void => setDragging(false);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [dragging]);

  /* aria-valuetext is announced on every step, which is correct for a screen
     reader driving the control. The settled summary is separate and debounced,
     so holding an arrow key produces one announcement rather than five. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLive(settledSummary(choice, billing));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [choice, billing]);

  /* THE TWO WRITERS. Both go through pricing.ts, which is the only place that
     knows how a band and a cohort relate, so neither control can invent its
     own answer and the pair cannot drift. */
  const commitTyped = useCallback(
    (raw: string) => {
      onChoiceChange(capacityFromTyped(raw, choice));
    },
    [choice, onChoiceChange],
  );

  const moveTo = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 0), LAST_INDEX);
      if (clamped === index) return;
      onChoiceChange(capacityFromSlider(clamped));
    },
    [index, onChoiceChange],
  );

  /* Arrows, Home and End come free from the native range input. Page Up and
     Page Down do not move a five-step range reliably across browsers, because
     the spec's "larger step" is a percentage of a range that is only four
     units wide, so they are implemented explicitly. */
  const onSliderKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "PageUp" && event.key !== "PageDown") return;
      event.preventDefault();
      moveTo(index + (event.key === "PageUp" ? PAGE_STEP : -PAGE_STEP));
    },
    [index, moveTo],
  );

  const note = describeCapacityNote(choice);

  /* Three numbers reach the CSS. --eg-i and --eg-n are what the capsule
     grid and the centre arithmetic read; --eg-p is the progress percentage
     the rule under the price reads. */
  const style = {
    "--eg-p": pct,
    "--eg-i": index,
    "--eg-n": BANDS.length,
  } as CSSProperties;

  return (
    <div
      className={cn(
        "eg-cap",
        focused && "is-focus",
        dragging && "is-drag",
        className,
      )}
      style={style}
    >
      <div className="eg-price">
        {/* ---------------------------------------------------------------
            The figure. Order is fixed: monthly, then per student, then the
            annual total. The whole point is that $400 lands before $4,000
            does.
            --------------------------------------------------------------- */}
        <div className="eg-price-figure">
          <PriceFigure band={band} billing={billing} />

          {/* The rule redraws under the price at the slider's own progress,
              so the figure and the control 300px away read as one object,
              and it carries the boundary sheen so the crossing is felt
              beside the number that changed rather than only at the thumb. */}
          <div className="eg-price-rule" aria-hidden="true">
            <i />
            {pulse > 0 ? (
              <b key={pulse} className={cn(pulseDown && "is-down")} />
            ) : null}
          </div>

          {/* The action sits directly under the price, not under the control.
              A buyer reads the figure and the next thing their eye lands on
              is the way to buy it, which is Adobe's card order exactly:
              price, disclosure, then the button. */}
          {actions ? <div className="eg-actions">{actions}</div> : null}
        </div>

        {/* ---------------------------------------------------------------
            The control
            --------------------------------------------------------------- */}
        <div className="eg-price-control">
          <BillingToggle
            labelId={billingLabelId}
            billing={billing}
            onChange={onBillingChange}
            band={band}
          />
          {/* THE HEAD. One line: the question on the left, the answer on the
              right. The old head was a form (a label, a boxed input, and the
              sentence "Optional. Or move the slider."), which read as a
              settings screen sitting above a control rather than as part of
              one. The input is unchanged in behaviour and is still the
              precise path for a buyer who already knows their cohort. */}
          <div className="eg-cap-head">
            <label className="eg-cap-head-label" htmlFor={typedId}>
              Your cohort
            </label>
            <div className="eg-cap-entry">
              <input
                id={typedId}
                /* The type stays text and the keypad comes from inputMode: a
                   native number spinner cannot be styled consistently and
                   silently accepts "e" and "-". */
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={draft}
                placeholder={cohortFieldPlaceholder(choice)}
                aria-describedby={noteId}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setDraft(next);
                  commitTyped(next);
                }}
                onBlur={() => {
                  /* Tidy the buyer's own typing into the page's number
                     format on the way out. It cannot change the value: the
                     draft has already been committed on every keystroke. */
                  setDraft(cohortFieldValue(choice));
                }}
              />
              <span className="eg-cap-entry-unit">students</span>
            </div>
          </div>

          <div className="eg-cap-slider">
            {/* The badge. Above the track, per Smashing's guidance: a finger
                covers a label placed below it on a phone. It carries the
                CEILING of the band, not a repeat of the cohort typed above
                it, so the two numbers on this control never say the same
                thing for two different reasons. The badge is CLAMPED inside
                the rail, because a centred badge on the last stop hangs past
                the panel edge; the caret is NOT clamped, it reads --eg-x
                directly, so it always points at the thumb. */}
            <div className="eg-cap-chip-rail" aria-hidden="true">
              <span className="eg-cap-chip">
                {/* Keyed on the band so React remounts the span and the
                    clipped rise restarts. No state, no timers. */}
                <span className="eg-swap">
                  <span key={band.id}>{ceilingOf(band)}</span>
                </span>
              </span>
              <span className="eg-cap-caret" />
            </div>

            <div className="eg-cap-rail">
              {/* THE LADDER. One capsule per band on a five-column grid, so
                  the tranche boundaries are the gaps and the structure cannot
                  be missed. The band the buyer is in is filled, taller and
                  glowing; the one ahead of them is tinted, which is the page's
                  argument made visible without a sentence. */}
              <div className="eg-cap-track" aria-hidden="true">
                {BANDS.map((stop, at) => (
                  <span
                    key={stop.id}
                    className={cn(
                      "eg-cap-seg",
                      at < index && "is-past",
                      at === index && "is-here",
                      at === index + 1 && "is-next",
                    )}
                  >
                    {/* The sheen, keyed on the crossing counter so it mounts,
                        runs once and is inert until the next boundary. It
                        sweeps across the capsule just entered. */}
                    {at === index && pulse > 0 ? (
                      <i
                        key={`wipe-${pulse}`}
                        className={cn("eg-cap-wipe", pulseDown && "is-down")}
                      />
                    ) : null}
                  </span>
                ))}

                {/* The ping. Same geometry as the thumb, expands and fades
                    once. Under the thumb, so the thumb lands inside it. */}
                {pulse > 0 ? (
                  <span key={`ping-${pulse}`} className="eg-cap-pulse" />
                ) : null}

                <div className="eg-cap-thumb">
                  <i />
                </div>
              </div>

              {/* The real input, stretched across the rail at zero opacity. It
                  receives every pointer and key event natively, which is where
                  keyboard support, assistive technology and form behaviour
                  come from. Everything above is decoration. */}
              <input
                id={sliderId}
                className="eg-cap-input"
                type="range"
                role="slider"
                min={0}
                max={LAST_INDEX}
                step={1}
                value={index}
                aria-label="Capacity commitment"
                aria-valuemin={0}
                aria-valuemax={LAST_INDEX}
                aria-valuenow={index}
                aria-valuetext={describeBandForAria(band, billing)}
                onChange={(event) => {
                  moveTo(Number(event.currentTarget.value));
                }}
                onKeyDown={onSliderKeyDown}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onPointerDown={() => setDragging(true)}
                onPointerUp={() => setDragging(false)}
              />
            </div>

            {/* The scale, on the same grid as the capsules, so every figure
                sits under its own band by construction at every width. Each
                column carries the band's ceiling AND its per-student rate in
                the cadence on screen, so the descent ($2.67, $2.33, $2.00,
                $1.80 a month; 27, 23, 20, 18 a year) is legible off the
                control with no prose beside it. Decorative: every figure here
                is also in the band cards below, which are the accessible path. */}
            <div className="eg-cap-scale" aria-hidden="true">
              {BANDS.map((stop, at) => {
                const per = formatPerStudent(stop, billing);
                return (
                  <span
                    key={stop.id}
                    className={cn(
                      "eg-cap-mark",
                      at < index && "is-past",
                      at === index && "is-here",
                      at === index + 1 && "is-next",
                    )}
                  >
                    <b>
                      {stop.capacity === null
                        ? `${formatCount(1000)}+`
                        : formatCount(stop.capacity)}
                    </b>
                    <i>{per ?? "Quote"}</i>
                  </span>
                );
              })}
            </div>

            {/* The headroom line. It says plainly whether the chosen band is
                above or below the cohort typed in the head, and it is the
                only sentence left in the control. The slot is reserved at one
                line so the cards below it do not shift when it appears. */}
            <p className="eg-cap-note" id={noteId}>
              {note}
            </p>
          </div>
        </div>

        {/* ---------------------------------------------------------------
            The bands. Inside the same card, under one hairline, wired as a
            radiogroup: picking one moves the slider, moving the slider checks
            one. Requirement 5 is that these are part of the control rather
            than a table beside it, so they share the card, the state and the
            crimson.
            --------------------------------------------------------------- */}
        <BandCards
          labelId={bandsLabelId}
          index={index}
          billing={billing}
          onPick={moveTo}
        />
      </div>

      {/* A printed range input is an unlabelled grey bar. This sentence is
          rendered from the same state and replaces it on paper. */}
      <p className="eg-cap-print">{describeBandForPrint(band, billing)}</p>

      {/* The settled summary, and nothing else. An additional static
          sr-only paragraph restating the selection was removed: it duplicated
          aria-valuetext, and a screen reader reads it as page content sitting
          under the control, which is worse than silence. */}
      <p id={liveId} className="e-sr-only" aria-live="polite">
        {live}
      </p>
    </div>
  );
}

/* ==========================================================================
   The figure
   ========================================================================== */

function PriceFigure({ band, billing }: { band: CapacityBand; billing: Billing }) {
  if (band.monthly === null || band.annual === null) {
    /* Linear's Enterprise pattern: the price slot literally contains a word,
       at the same size and weight as a number. The per-student line is omitted
       rather than filled with a dash, because a dash reads as a value of
       nothing rather than as an answer we will send you. */
    return (
      <div className="eg-price-stack">
        <div className="eg-price-lead">
          <span className="eg-price-money is-word">Quote</span>
        </div>
        <p className="eg-price-annual">Priced on your cohort.</p>
      </div>
    );
  }

  const monthly = formatMoney(band.monthly);
  const annual = formatMoney(band.annual);
  /* THE SAVING IS REAL, AND IT IS WORTH SAYING PLAINLY.
     Annual is exactly ten times monthly, so paying monthly across the same
     twelve month term costs two months more: $4,800 against $4,000 on the
     entry band. Stated as the amount rather than as "two months free",
     because a reader can check $800 against the two figures on the same
     screen and cannot check a phrase. */
  const saved = formatMoney((band.monthly ?? 0) * 12 - (band.annual ?? 0));

  if (billing === "annual") {
    /* The buyer asked to see the annual figure, so it leads here. The monthly
       equivalent stays directly beneath it, at body size, so nothing about
       the offer is hidden by the choice of cadence. */
    return (
      <div className="eg-price-stack">
        <div className="eg-price-lead">
          <span className="eg-price-money">{annual}</span>
          <span className="eg-price-unit">per year</span>
        </div>
        <p className="eg-price-per">{describeEquivalence(band, "annual")}</p>
        {/* No monthly equivalent beside the annual price (Thomas, Sep 21
            2026). The toggle above already shows the monthly figure, so
            restating it under the annual one gave a reader two numbers to
            reconcile where the offer is one. What stays is the part the
            annual price cannot say about itself: what it saves. */}
        <p className="eg-price-annual">
          <b className="eg-price-save">{saved} less than paying monthly</b>
        </p>
      </div>
    );
  }

  return (
    <div className="eg-price-stack">
      <div className="eg-price-lead">
        <span className="eg-price-money">{monthly}</span>
        <span className="eg-price-unit">per month</span>
      </div>
      <p className="eg-price-per">{describeEquivalence(band, "monthly")}</p>
      {/* Not "$4,000 billed annually": beside a monthly price that read as
          how this plan is billed (Thomas, Sep 14 2026). The annual price is
          one tap away, on the toggle with its Save tag. */}
      <p className="eg-price-annual">Billed monthly</p>
    </div>
  );
}

/* ==========================================================================
   The band cards

   A radiogroup with roving tabindex, which is the WAI-ARIA pattern for a set
   of mutually exclusive options. Five buttons each carrying tabindex=0 would
   put five stops in the tab order for one decision; roving tabindex puts one
   there and hands the arrow keys the rest, which is also what the slider does
   twenty pixels above, so the two halves of the control answer to the same
   keys.
   ========================================================================== */

function BandCards({
  labelId,
  index,
  billing,
  onPick,
}: {
  labelId: string;
  index: number;
  billing: Billing;
  onPick: (next: number) => void;
}) {
  const cards = useRef<(HTMLButtonElement | null)[]>([]);

  const go = (next: number): void => {
    const wrapped = (next + BANDS.length) % BANDS.length;
    onPick(wrapped);
    cards.current[wrapped]?.focus();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, at: number): void => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        go(at + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        go(at - 1);
        break;
      case "Home":
        event.preventDefault();
        go(0);
        break;
      case "End":
        event.preventDefault();
        go(LAST_INDEX);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <p id={labelId} className="e-sr-only">
        Capacity bands
      </p>
      <div className="eg-tiers" role="radiogroup" aria-labelledby={labelId}>
        {BANDS.map((stop, at) => {
          const on = at === index;
          const per = formatPerStudent(stop, billing);
          const lead =
            stop.monthly === null
              ? "Quote"
              : billing === "annual"
                ? formatMoney(stop.annual ?? 0)
                : formatMoney(stop.monthly);
          const unit =
            stop.monthly === null ? null : billing === "annual" ? "a year" : "a month";

          return (
            <button
              key={stop.id}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              ref={(node) => {
                cards.current[at] = node;
              }}
              className={cn("eg-tier", on && "is-on")}
              onClick={() => onPick(at)}
              onKeyDown={(event) => onKeyDown(event, at)}
            >
              <span className="eg-tier-name">{stop.shortName}</span>
              <span className="eg-tier-price">
                <b>{lead}</b>
                {unit ? <i>{unit}</i> : null}
              </span>
              <span className="eg-tier-per">
                {per === null ? "Priced on your cohort" : `${per} per student`}
              </span>
              {/* The "who it fits" line ("One program, one office...") left
                  the cards on Sep 14 2026 at Thomas's ask. `fit` stays on the
                  rate card, which the Worker's rate-card route still serves. */}
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ==========================================================================
   The billing toggle

   Two buttons, not a switch. A switch has an on state and an off state, and
   neither of these is off. The saving is stated as its mechanism, because
   annual is exactly ten times monthly and any reader can check that against
   the two figures on the same screen. "Save 17%" is not checkable and is
   therefore worth less than the arithmetic it hides.
   ========================================================================== */

function BillingToggle({
  labelId,
  billing,
  onChange,
  band,
}: {
  labelId: string;
  billing: Billing;
  onChange: (billing: Billing) => void;
  /** The selected band: the Save tag reads its own saving off it. */
  band: CapacityBand;
}) {
  /* The tag (Thomas, Sep 9 2026: "a dynamic Save 17% little icon ... not in
     the content of the toggle, but the number should adjust based on what
     is selected"). It sits on the group's corner, outside both buttons,
     and the figure is computed from the selected band's own monthly and
     annual prices, never typed. The quoted band has no prices and no tag. */
  const saving = annualSavingPercent(band);
  return (
    <div className="eg-toggle-wrap">
      <p id={labelId} className="e-sr-only">
        Billing frequency
      </p>
      <div className="eg-toggle" role="group" aria-labelledby={labelId}>
        <button
          type="button"
          aria-pressed={billing === "monthly"}
          onClick={() => onChange("monthly")}
        >
          Monthly
        </button>
        <button
          type="button"
          aria-pressed={billing === "annual"}
          onClick={() => onChange("annual")}
        >
          Annual
        </button>
      </div>
      {saving !== null ? (
        <span className="eg-toggle-badge">
          Save {saving}%<span className="e-sr-only"> with annual billing</span>
        </span>
      ) : null}
    </div>
  );
}

/* ==========================================================================
   Helpers
   ========================================================================== */

/**
 * What the badge above the thumb says: the CEILING of the band, never the
 * cohort typed in the head. Two numbers on one control that read the same at
 * rest and diverge under use is the kind of thing a buyer notices once and
 * never trusts again.
 */
function ceilingOf(band: CapacityBand): string {
  return band.capacity === null
    ? `Over ${formatCount(1000)}`
    : `Up to ${formatCount(band.capacity)}`;
}

/** The settled announcement. One sentence, the whole offer in the cadence on
 *  screen, no "3 of 5". What a screen reader hears on every step is
 *  describeBandForAria itself, which pricing.ts builds for both cadences. */
function settledSummary(choice: CapacityChoice, billing: Billing): string {
  const band = bandById(choice.bandId);
  const note = describeCapacityNote(choice);
  return note === null
    ? describeBandForAria(band, billing)
    : `${describeBandForAria(band, billing)}. ${note}`;
}
